import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Download, Play, Plus, X } from "lucide-react";
import { toast } from "sonner";

import { AccountMenu } from "@/auth/AccountMenu";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DECK_FILE_EXTENSION } from "@/lib/deckFile";
import { isDeckOpenElsewhere } from "@/lib/deckSession";
import { desktopShell } from "@/lib/desktopBridge";
import { getDeck, putDeck } from "@/lib/idb";
import { cn } from "@/lib/utils";
import { createNewDeck } from "@/store/bootstrap";
import { setTitle } from "@/store/documentSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { closeDesktopTab, refreshTitles, useDesktopTabs } from "@/desktop/tabs";

interface DesktopTabsProps {
  // The editor's own actions, moved here from the TopBar: in the shell this row IS the
  // window's chrome, and a second full-width bar above it would be pure duplication of what
  // the OS title bar and the native menu already say.
  onPresent?: () => void;
  onExportPdf?: () => void;
  onExportHtml?: () => void;
  onExportDeckFile?: () => void;
  // §12.5.2 This page does not hold the deck's write lock: the title is not editable here.
  readOnly?: boolean;
}

/**
 * §14.7.1 The tab strip — and, in the shell, the whole of the editor's chrome.
 *
 * One tab is one deck at one `/d/:deckId` route, so clicking a tab is a navigation and
 * nothing more. Renaming happens here too: the tab header is where the document's name is,
 * so it is where you change it (double-click, or the ⌄ menu).
 */
export function DesktopTabs({
  onPresent,
  onExportPdf,
  onExportHtml,
  onExportDeckFile,
  readOnly = false,
}: DesktopTabsProps = {}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { deckIds, activeDeckId, titles, ready } = useDesktopTabs();
  // The open deck's title comes from the store, not from the tab store's copy of the §12.3
  // index: BroadcastChannel never delivers a page its own posts, so a rename made here would
  // otherwise not show up in this window's own strip until something else refreshed it.
  const openDeckId = useAppSelector((s) => s.document.id);
  const openTitle = useAppSelector((s) => s.document.title);
  const [renaming, setRenaming] = useState<string | null>(null);

  const titleOf = useCallback(
    (deckId: string) =>
      (deckId === openDeckId ? openTitle : titles[deckId]) || "Untitled deck",
    [openDeckId, openTitle, titles],
  );

  // The row is the shell's only chrome, so it renders as soon as we know we are in the shell.
  // `ready` gates the TABS alone — holding the whole bar back until `userData` has been read
  // would flash the editor with no toolbar at all on every launch.
  if (!desktopShell()) return null;

  const close = (deckId: string) => {
    const next = closeDesktopTab(deckId);
    if (deckId !== activeDeckId) return;
    navigate(next ? `/d/${next}` : "/", { replace: true });
  };

  // Renaming the deck this window is editing is an ordinary document edit — the persistence
  // listener writes it under the lock we already hold. Renaming a deck that is merely OPEN in
  // a tab is a storage write instead, and has to take the same precaution the documents home
  // does: bumping `rev` under another window's writer costs that window its next save.
  const commitRename = async (deckId: string, next: string) => {
    setRenaming(null);
    const title = next.trim();
    if (!title || title === titleOf(deckId)) return;

    if (deckId === openDeckId) {
      if (readOnly) return;
      dispatch(setTitle(title));
      return;
    }
    if (await isDeckOpenElsewhere(deckId)) {
      toast.error("That document is open in another window", {
        description: "Rename it there, or close that window first.",
      });
      return;
    }
    const deck = await getDeck(deckId);
    if (!deck) {
      toast.error("That document is no longer here");
      return;
    }
    const result = await putDeck({
      ...deck,
      title,
      meta: { ...deck.meta, updatedAt: new Date().toISOString() },
    });
    if (result === "conflict") {
      toast.error("That document changed while you were renaming it");
    }
    await refreshTitles();
  };

  return (
    <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-2 py-1">
      <div className="flex min-w-0 flex-1 items-stretch gap-1 overflow-x-auto">
        {(ready ? deckIds : []).map((deckId) => {
          const active = deckId === activeDeckId;
          const label = titleOf(deckId);
          return (
            <div
              key={deckId}
              className={cn(
                "group flex min-w-0 max-w-56 shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-sm",
                active
                  ? "border-slate-300 bg-white text-slate-900 shadow-sm"
                  : "border-transparent text-slate-600 hover:bg-slate-200/60",
              )}
            >
              {renaming === deckId ? (
                <RenameField
                  initial={label}
                  onCommit={(value) => void commitRename(deckId, value)}
                  onCancel={() => setRenaming(null)}
                />
              ) : (
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate text-left outline-none"
                  aria-current={active ? "page" : undefined}
                  onClick={() => navigate(`/d/${deckId}`)}
                  onDoubleClick={() => setRenaming(deckId)}
                  // Middle-click closes, as in every other tabbed app.
                  onAuxClick={(e) => {
                    if (e.button === 1) close(deckId);
                  }}
                  title={label}
                >
                  {label}
                </button>
              )}
              <button
                type="button"
                aria-label={`Close ${label}`}
                className="rounded p-0.5 text-slate-400 opacity-0 transition-opacity hover:bg-slate-200 hover:text-slate-700 focus-visible:opacity-100 group-hover:opacity-100"
                onClick={() => close(deckId)}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 self-center text-slate-500"
          aria-label="New document"
          onClick={() =>
            void createNewDeck().then((id) => navigate(`/d/${id}`))
          }
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* The editor's actions, one icon each. §14.6.6 The export gate does not apply in the
          desktop runtime, so these run straight away — no sign-in wall in front of the user's
          own deck on their own disk. */}
      <TooltipProvider delayDuration={400}>
        <div className="flex shrink-0 items-center gap-1">
          {(onExportPdf || onExportHtml || onExportDeckFile) && (
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-slate-600"
                      aria-label="Export"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>Export</TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="end">
                {onExportPdf && (
                  <DropdownMenuItem onClick={onExportPdf}>PDF</DropdownMenuItem>
                )}
                {onExportHtml && (
                  <DropdownMenuItem onClick={onExportHtml}>
                    HTML
                  </DropdownMenuItem>
                )}
                {onExportDeckFile && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onExportDeckFile}>
                      Deck file ({DECK_FILE_EXTENSION})
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {onPresent && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-slate-600"
                  aria-label="Presentation mode"
                  onClick={onPresent}
                >
                  <Play className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Presentation mode</TooltipContent>
            </Tooltip>
          )}
          {/* §9.3 Signed out this is a single person icon rather than a "Sign in" button —
              the row is chrome, not a call to action. */}
          <AccountMenu iconOnly size="sm" />
        </div>
      </TooltipProvider>
    </div>
  );
}

/**
 * The inline rename field. Self-selecting, commits on Enter or blur, abandons on Escape —
 * the behaviour of every other tab title in every other app, which is the point.
 */
function RenameField({
  initial,
  onCommit,
  onCancel,
}: {
  initial: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  // Escape must not reach the commit-on-blur below, so cancellation is latched here rather
  // than inferred from the blur that follows it.
  const cancelled = useRef(false);

  useEffect(() => {
    ref.current?.select();
  }, []);

  return (
    <input
      ref={ref}
      value={value}
      autoFocus
      aria-label="Document name"
      className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-1 text-sm outline-none"
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (!cancelled.current) onCommit(value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onCommit(value);
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancelled.current = true;
          onCancel();
        }
      }}
    />
  );
}
